import * as React from "react";
import * as yup from "yup";
import * as Comp from "../../../../components";
import { ComponentWithValidatedForm } from "../../../../components/validation";
import * as Forms from "../../../../forms";
import * as M from "../../../../models";
import { Api } from "../../../../services/Api";
import { Utility } from "../../../../utils";

interface OwnProps {
    api: Api;
    eventId: number;
    onComplete: () => void;
}

interface OwnState {
    abstractType?: M.Abstract.AbstractTypeEdit;
    show: boolean;
}

interface TestEmailData {
    abstract: M.Abstract.ScheduledAbstractItem | M.Generic.ReactSelectOption | null;
    emails: string;
}

class TestEmail implements M.Validation.FormValidationModel<TestEmailData> {
    public data: TestEmailData;
    public rules: yup.Schema<TestEmailData>;

    public constructor() {
        this.data = {
            abstract: null,
            emails: "",
        };
        this.rules = yup.object<TestEmailData>().shape({
            abstract: Forms.Generic.ReactSelectOptionSchema.label("Abstract").nullable(true),
            emails: yup.string().required().label("Email addresses").min(0, "Invalid ID"),
        });
    }
}

export class TestEmailModal extends ComponentWithValidatedForm<OwnProps, OwnState> {

    private _isMounted = false;
    private _searchTimeout: NodeJS.Timer;

    public constructor(props: any) {
        super(props);

        const formModel = new TestEmail();
        this.state = {
            ...this.getInitialState(formModel),
            show: false,
        };

        this.onSubmit = this.onSubmit.bind(this);
    }

    public componentDidMount() {
        this._isMounted = true;
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public close() {
        this.setState({ ...this.state, show: false });
    }

    public show(abstractType: M.Abstract.AbstractTypeEdit) {
        const formModel = new TestEmail();
        this.setState({ ...this.state,
            ...this.getInitialState(formModel),
            abstractType,
            show: true,
        });
    }

    public render() {
        return <Comp.Modal.ModalForm
            formComponent={this}
            labelSave="Send test"
            onCancel={() => this.close()}
            processing={this.state.formProcessing}
            show={this.state.show}
            title="Send a test"
        >
            <Comp.Validation.ValidatedForm
                formComponent={this}
                model={this.state.formModel.data}
                onValidSubmit={this.onSubmit}
                onCustomValidation={() => this.validateEmails()}
                schema={this.state.formModel.rules}
                ref={(ref: any) => this.form = ref}
            >

                <Comp.Editors.TextAreaField
                    formComponent={this}
                    name="emails"
                    hint="Use commas to separate multiple emails"
                    label="Send a test to"
                />

                <Comp.Editors.SelectField
                    description="Optional. If left blank, a random abstract for the selected abstract type will be chosen."
                    formComponent={this}
                    isAsync
                    name="abstract"
                    select={{
                        // cache: false,
                        ignoreCase: false,
                        loadOptions: (search: string) => this.searchAbstracts(search),
                        optionRenderer: (x: M.Abstract.ScheduledAbstractItem) => <p>{this.renderAbstractDetails(x)}</p>,
                        searchPromptText: "Start typing and select from suggested abstracts",
                        valueRenderer: (x: M.Abstract.ScheduledAbstractItem) => <p dangerouslySetInnerHTML={{ __html: x.title }} />,
                    }}
                />

            </Comp.Validation.ValidatedForm>
        </Comp.Modal.ModalForm>;
    }

    private getEmailsAsArray(model: TestEmailData) {
        return model.emails
            .replace(/(,\s{0,})|\r\n|\r|\n/gm, "|")
            .split("|").map((x) => x.trim()).filter((x) => x !== "");
    }

    private async onSubmit(model: TestEmailData) {
        if (!this.state.abstractType)
            return;

        this.toggleProcessing(true);
        const emails = this.getEmailsAsArray(model);

        const eventId = this.props.eventId;
        const result = await this.props.api.post<any>(Api.endpoints.admin.abstract.invitationEmailTest.withUrlParams({ eventId }), {
            body: JSON.stringify({
                abstractId: model.abstract
                    ? (model.abstract as M.Abstract.ScheduledAbstractItem).id
                    : null,
                abstractTypeId: this.state.abstractType.id,
                emails,
            }),
        });

        if (!this._isMounted)
            return;

        if (result.isSuccess && result.data) {
            this.close();
            this.props.onComplete();
        }
        else {
            this.setFormErrors(M.Result.Result.errorsOrMessage(result));
        }

        this.toggleProcessing(false);
    }

    private renderAbstractDetails(x: M.Abstract.ScheduledAbstractItem) {
        return <>
            #{x.id} - <span dangerouslySetInnerHTML={{ __html: x.title }} />
            <br />
            <span className="mute">
                {x.typeShortName}{x.placement || x.classification ? " - " : ""}
                {Utility.joinSkipEmpty([x.placement, x.classification], ", ")}{" by "}
                <strong>{Utility.joinSkipEmpty(x.primaryAuthors.map((author) => M.Generic.PersonHelper.getFullNameWithSuffix(author)), ", ")}</strong>
            </span>
        </>;
    }

    private searchAbstracts(search: string) {
        if (!search || search.length < 2)
            return Promise.resolve({ options: [] });

        return new Promise((resolve, reject) => {
            // delay the ajax call as to not make a call for each letter typed.
            if (this._searchTimeout !== undefined)
                clearTimeout(this._searchTimeout);

            this._searchTimeout = setTimeout(() => {
                const queryParams = { queryParams: {
                    eventId: this.props.eventId,
                    q: search,
                    statusIds: M.Abstract.AbstractStatuses.SubmittedValues.map((v) => v.id).join(","),
                    typeIds: this.state.abstractType ? this.state.abstractType.id : null,
                }};

                this.props.api.callApi(Api.endpoints.admin.abstract.search, queryParams)
                    .then((result) => {
                        if (result.isSuccess && result.data) {
                            let options = result.data.map((x) => {
                                return Utility.toReactSelectOption(x, x.title, x.id);
                            });

                            options = [...options];
                            resolve({ options });
                        }
                        else
                            console.error(result.errors.join("; "));
                    });
            }, 375);
        }).then((data) => {
            return data;
        });
    }

    private validateEmails() {
        const model = this.getFormModelData<TestEmailData>();
        const emails = this.getEmailsAsArray(model);

        if (!emails.length)
            return [];

        const fieldErrors: M.Validation.IFieldValidationError[] = [];
        for (const addr of emails)
            if (!Utility.isValidEmail(addr))
                fieldErrors.push(new M.Validation.FieldValidationError("emails", `${addr} is not a valid email address.`));

        return fieldErrors;
    }
}



// WEBPACK FOOTER //
// ./src/containers/adminAbstracts/invitationEmails/components/TestEmailModal.tsx